import { SimpleGrid, useToast } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, isEqual, omit } from 'lodash';

import MevoInput from 'src/components/Input';
import Typography from 'src/components/Typography';
import SelectGender from 'src/components/UI/SelectGender';
import AlertIcon from 'src/components/Icons/AlertIcon';
import { Box } from 'src/components/UI/Box';
import { Button } from 'src/components/UI/Button';
import { Checkbox } from 'src/components/UI/Checkbox';
import { Flex } from 'src/components/UI/Flex';
import { FormControl } from 'src/components/UI/FormControl';
import { MASKS } from 'src/components/UI/Input/MaskedInput';
import { Skeleton } from 'src/components/UI/Skeleton';

import { Yup } from 'src/helpers/Yup';
import { validateCPF } from 'src/helpers/cpf';
import { onlyDigits } from 'src/helpers/string';
import {
  DateFromFormat,
  DateFromISOToBrazilianFormat,
  DateFromISOToJSDate,
  validateDateFromBrazilianForm,
} from 'src/helpers/Date';

import { DialogWrappedComponentProps, useFeedbackContext } from 'src/providers/FeedbackProvider';
import { usePrescriberContext, USER_GROUPS } from 'src/providers/PrescriberProvider';

import {
  AutocompletePatientSuccessResponse,
  GENDER,
  LGPDDeniedReason,
  PatientBasicData,
  PatientService,
} from 'src/services/patient';
import { PatientAlreadyCreatedDialog } from 'src/containers/CreatePartialPatient/PatientAlreadyCreatedDialog';
import { InitPrescriptionDialog } from 'src/containers/CreatePartialPatient/InitPrescriptionDialog';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { PersonalDataFormSkeleton } from '../skeletons/PersonalDataFormSkeleton';
import { usePatientAutocomplete } from 'src/hooks/usePatientAutocomplete';
import { calcAgeFromDateOfBirth } from 'src/helpers/calcAgeFromDateOfBirth';
import {
  trackPatientRegistrationView,
  trackPatientSaveEditClick,
  trackPatientSaveNewClick,
} from 'src/services/tracking/events';

interface PatientBasicDataForm extends PatientBasicData {
  hasDocument: boolean;
  isFormOnEditMode: boolean;
}

interface PatientFormLocationState {
  cpf?: string;
  autocompleteData?: AutocompletePatientSuccessResponse;
}

const PersonalDataSchema = Yup.object().shape({
  fullName: Yup.string().required('Informe o nome completo.'),
  hasDocument: Yup.bool().nullable(),
  document: Yup.string().when('hasDocument', {
    is: true,
    then: (schema: any) =>
      schema
        .required('Informe um número de CPF válido.')
        .test('validate cpf', 'Informe um número de CPF válido.', (schema: string) => {
          return validateCPF(schema || '');
        }),
    otherwise: (schema: any) => schema.nullable(),
  }),
  cellphoneNumber: Yup.string()
    .required('Informe um número de telefone válido.')
    .test('validate phoneNumber', 'Informe um número de celular válido.', function (phoneNumber) {
      if (!phoneNumber) return false;
      return onlyDigits(phoneNumber).length === 11;
    }),
  birthDate: Yup.string().test('match', 'Informe uma data de nascimento válida.', (birthDate) => {
    if (!birthDate) return true;

    return validateDateFromBrazilianForm(birthDate);
  }),
  sex: Yup.string().nullable(),
});

const initialValues: PatientBasicDataForm = {
  fullName: '',
  cellphoneNumber: '',
  birthDate: '',
  sex: GENDER.NOT_INFORMED,
  document: '',
  documentType: 'cpf',
  hasDocument: true,
  _id: null,
  isFormOnEditMode: true,
  active: false,
  lastAttendance: '',
};

const MINIMUM_AGE_FOR_MINOR = 18;
const TOAST_DURATION_SUCCESS = 4000;
const TOAST_DURATION_SUCCESS_EDIT = 4000;
const TOAST_DURATION_ERROR = 2000;
const UNDO_TOAST_DURATION = 8000;

const FIELD_IDS = {
  DOCUMENT: 'document',
  FULL_NAME: 'fullName',
  BIRTH_DATE: 'birthDate',
  CELLPHONE: 'cellphoneNumber',
} as const;

interface BasicDataFormProps {
  onCreatePatient(patientId: string): void;
  onEditPatient(patientName: string, patientGender: GENDER): void;
  isLoadingData: boolean;
  formData: PatientBasicData | null;
}

const isEditingPatientWithCPF = (patientId: string | null, initialCPF: string): boolean => {
  return !!patientId && !!initialCPF;
};

const isPatientMinor = (birthDate: string): boolean => {
  if (!birthDate) return false;
  const birthDateObj = DateFromISOToJSDate(birthDate);
  const age = calcAgeFromDateOfBirth(birthDateObj);
  return age !== null && age.years < MINIMUM_AGE_FOR_MINOR;
};

const shouldDisableCPFField = (hasDocumentEnabled: boolean, patientId: string | null, initialCPF: string): boolean => {
  return !hasDocumentEnabled || isEditingPatientWithCPF(patientId, initialCPF);
};

const shouldCallAutocomplete = (
  cpf: string,
  hasDocument: boolean,
  patientId: string | null,
  initialCPF: string
): boolean => {
  return validateCPF(cpf) && hasDocument && !isEditingPatientWithCPF(patientId, initialCPF);
};

export function BasicDataForm(props: BasicDataFormProps) {
  const [defaultValues, setDefaultValues] = useState<PatientBasicDataForm>(initialValues);
  const [showMinorWarning, setShowMinorWarning] = useState(false);
  const [showApiUnavailableWarning, setShowApiUnavailableWarning] = useState(false);
  const [autocompletedCPF, setAutocompletedCPF] = useState<string | null>(null);
  const [shouldFocusAfterLoading, setShouldFocusAfterLoading] = useState(false);
  const [lgpdBirthdateRequired, setLgpdBirthdateRequired] = useState(false);
  const [shouldShowUndoToast, setShouldShowUndoToast] = useState(true);
  const [lastValidAutocompleteData, setLastValidAutocompleteData] = useState<{
    fullName: string;
    birthDate: string;
    cellphoneNumber: string;
    sex: GENDER;
    cpf: string;
    showMinorWarning: boolean;
  } | null>(null);
  const previousFormValuesRef = useRef<PatientBasicDataForm | null>(null);
  const previousCPFRef = useRef<string>('');
  const isInitialLoadRef = useRef(true);
  const undoToastIdRef = useRef<any>(null);

  const fullNameInputRef = useRef<HTMLInputElement>(null);
  const birthDateInputRef = useRef<HTMLInputElement>(null);
  const cellphoneInputRef = useRef<HTMLInputElement>(null);

  const { prescriberId, setUserDoesNotHaveAnyPatients, startPrescrition, userGroup } = usePrescriberContext();
  const { openToast, openBaseDialog } = useFeedbackContext();
  const toast = useToast();

  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as PatientFormLocationState | null;

  useEffect(() => {
    trackPatientRegistrationView(prescriberId || undefined);
  }, [prescriberId]);

  const formik = useFormik<PatientBasicDataForm>({
    initialValues: initialValues,
    validationSchema: PersonalDataSchema,
    validateOnChange: false,
    async onSubmit(values) {
      try {
        if (!prescriberId) return;
        const date = DateFromFormat(values.birthDate || '', 'dd/MM/yyyy');
        const document = onlyDigits(values.document);

        if (values.isFormOnEditMode && values._id) {
          trackPatientSaveEditClick(prescriberId || undefined);

          await PatientService.updatePatient(
            { ...values, birthDate: DateTime.fromISO(date).toJSDate(), document },
            prescriberId,
            values._id
          );
          setUserDoesNotHaveAnyPatients(false);

          openToast('Paciente editado com sucesso.', { type: 'success', duration: TOAST_DURATION_SUCCESS_EDIT });

          setShouldShowUndoToast(false);

          if (defaultValues.fullName !== values.fullName || defaultValues.sex !== values.sex) {
            props.onEditPatient(values.fullName, values.sex);
          }
        } else {
          trackPatientSaveNewClick(prescriberId || undefined);

          const patient = await PatientService.createPartialPatient(
            { ...values, birthDate: DateTime.fromISO(date).toJSDate(), document },
            prescriberId
          );
          setUserDoesNotHaveAnyPatients(false);

          openToast('Paciente cadastrado com sucesso.', { type: 'success', duration: TOAST_DURATION_SUCCESS });

          setShouldShowUndoToast(false);

          if (userGroup === USER_GROUPS.assistant) {
            props.onCreatePatient(patient._id);
            return;
          }

          await openBaseDialog(
            (dialogProps: DialogWrappedComponentProps) => (
              <InitPrescriptionDialog
                onCancel={() => {
                  dialogProps.onClose();
                  props.onCreatePatient(patient._id);
                }}
                onConfirm={() => {
                  startPrescrition(patient._id);
                  dialogProps.onClose();
                }}
              />
            ),
            {
              width: '300px',
              height: '330px',
            }
          );
        }
      } catch (error: any) {
        const status = get(error, 'response.status', null);
        const message = get(error, 'response.data.message', '');

        if (status === 412 && message.includes('Paciente já cadastrado')) {
          await openBaseDialog(
            (dialogProps: DialogWrappedComponentProps) => (
              <PatientAlreadyCreatedDialog
                onCancel={dialogProps.onClose}
                onConfirm={() => {
                  navigate('/inicio/receita');
                  dialogProps.onClose();
                }}
              />
            ),
            {
              width: '270px',
              height: '356px',
            }
          );
          return;
        }

        openToast(
          'Ops.. Um problema inesperado ocorreu. Aguarde alguns minutos e tente novamente. Caso o problema persista, fale com a gente.',
          { type: 'error', duration: TOAST_DURATION_ERROR }
        );
      }
    },
  });

  const { fetchPatientByCPF, loading: loadingAutocomplete } = usePatientAutocomplete({
    prescriberId,
    onSuccess: (data: AutocompletePatientSuccessResponse) => {
      setShowApiUnavailableWarning(false);
      setLgpdBirthdateRequired(false);

      const { patientData } = data;
      const birthDateFormatted = patientData.birthDate ? DateFromISOToBrazilianFormat(patientData.birthDate) : '';

      const isMinor = isPatientMinor(patientData.birthDate);
      setShowMinorWarning(isMinor);

      setLastValidAutocompleteData({
        fullName: patientData.fullName,
        birthDate: birthDateFormatted,
        cellphoneNumber: patientData.cellphoneNumber || '',
        sex: patientData.sex,
        cpf: formik.values.document,
        showMinorWarning: isMinor,
      });

      setAutocompletedCPF(formik.values.document);
      previousCPFRef.current = formik.values.document;

      const newValues = {
        ...formik.values,
        fullName: patientData.fullName,
        birthDate: birthDateFormatted,
        cellphoneNumber: patientData.cellphoneNumber || '',
        sex: patientData.sex,
      };

      formik.setValues(newValues);

      setShouldShowUndoToast(true);

      setShouldFocusAfterLoading(true);
    },
    onNotFound: () => {
      setShowApiUnavailableWarning(true);
      setLgpdBirthdateRequired(false);

      setShouldFocusAfterLoading(true);
    },
    onLGPDDenied: (reason: LGPDDeniedReason) => {
      setLastValidAutocompleteData({
        fullName: '',
        birthDate: '',
        cellphoneNumber: '',
        sex: GENDER.NOT_INFORMED,
        cpf: formik.values.document,
        showMinorWarning: true,
      });
      setAutocompletedCPF(formik.values.document);
      previousCPFRef.current = formik.values.document;

      setShouldShowUndoToast(true);

      if (reason === 'lgpd_birthdate_required') {
        setLgpdBirthdateRequired(true);
        setShowMinorWarning(true);
        setShowApiUnavailableWarning(false);
        setShouldFocusAfterLoading(true);
      } else if (reason === 'lgpd_birthdate_mismatch' || reason === 'lgpd_patient_under_12') {
        setLgpdBirthdateRequired(false);
        setShowMinorWarning(true);
        setShowApiUnavailableWarning(false);
      }
    },
  });

  const setValues = useMemo(() => formik.setValues, [formik.setValues]);

  const isSameData = useMemo(() => {
    return isEqual(omit(defaultValues, ['_id']), omit(formik.values, ['_id']));
  }, [defaultValues, formik.values]);

  const isSubmitButtonDisabled = formik.values.isFormOnEditMode && isSameData;

  const focusNextEmptyField = useCallback((values: PatientBasicDataForm) => {
    const focusInputById = (id: string): boolean => {
      const element = document.getElementById(id) as HTMLInputElement | null;

      if (!element) return false;

      element.focus();
      return true;
    };

    const shouldFocusBirthDate = !values.birthDate;
    const shouldFocusFullName = !values.fullName;
    const shouldFocusCellphone = !values.cellphoneNumber;

    if (shouldFocusBirthDate) {
      focusInputById('birthDate');
    } else if (shouldFocusFullName) {
      focusInputById('fullName');
    } else if (shouldFocusCellphone) {
      focusInputById('cellphoneNumber');
    }
  }, []);

  useEffect(() => {
    if (!loadingAutocomplete && shouldFocusAfterLoading) {
      requestAnimationFrame(() => {
        focusNextEmptyField(formik.values);
      });
      setShouldFocusAfterLoading(false);
    }
  }, [loadingAutocomplete, shouldFocusAfterLoading, focusNextEmptyField, formik.values]);

  const showUndoToast = () => {
    if (!shouldShowUndoToast) {
      return;
    }

    if (undoToastIdRef.current) {
      toast.close(undoToastIdRef.current);
    }

    undoToastIdRef.current = toast({
      duration: UNDO_TOAST_DURATION,
      isClosable: true,
      position: 'top-right',
      render: () => (
        <Flex
          bgColor="#FFD700"
          borderRadius="10px"
          padding="16px 20px"
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
          alignItems="center"
          justifyContent="space-between"
          minW="300px"
        >
          <Flex alignItems="center" gap="12px">
            <AlertIcon variantColor="notification.alert" width={20} />
            <Typography model="body" size="normal" weight="bold" color="#3D1454">
              CPF desassociado
            </Typography>
          </Flex>
          <Box as="button" onClick={handleUndo} cursor="pointer" _hover={{ opacity: 0.7 }} transition="opacity 0.2s">
            <Typography model="body" size="normal" weight="bold" color="#3D1454">
              Desfazer
            </Typography>
          </Box>
        </Flex>
      ),
    });
  };

  const handleUndo = () => {
    if (lastValidAutocompleteData) {
      const restoredValues = {
        ...formik.values,
        fullName: lastValidAutocompleteData.fullName,
        birthDate: lastValidAutocompleteData.birthDate,
        cellphoneNumber: lastValidAutocompleteData.cellphoneNumber,
        sex: lastValidAutocompleteData.sex,
        document: lastValidAutocompleteData.cpf,
        hasDocument: true,
      };

      formik.setValues(restoredValues);

      formik.setErrors({
        ...formik.errors,
        document: undefined,
      });

      setAutocompletedCPF(lastValidAutocompleteData.cpf);
      previousCPFRef.current = lastValidAutocompleteData.cpf;
      setShowMinorWarning(lastValidAutocompleteData.showMinorWarning);

      if (lastValidAutocompleteData.showMinorWarning && !lastValidAutocompleteData.fullName) {
        setLgpdBirthdateRequired(true);
      }

      requestAnimationFrame(() => {
        focusNextEmptyField(restoredValues);
      });

      if (undoToastIdRef.current) {
        toast.close(undoToastIdRef.current);
        undoToastIdRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      previousCPFRef.current = formik.values.document;
      return;
    }

    const cpfDigits = onlyDigits(formik.values.document);

    if (autocompletedCPF && formik.values.document !== autocompletedCPF) {
      setShowMinorWarning(false);
      setShowApiUnavailableWarning(false);
      setAutocompletedCPF(null);

      const hasExistingCPF = isEditingPatientWithCPF(formik.values._id, defaultValues.document);
      if (!hasExistingCPF) {
        formik.setValues({
          ...formik.values,
          fullName: '',
          birthDate: '',
          cellphoneNumber: '',
          sex: GENDER.NOT_INFORMED,
        });

        if (lastValidAutocompleteData) {
          showUndoToast();
        }
      }
    }

    previousCPFRef.current = formik.values.document;

    if (shouldCallAutocomplete(cpfDigits, formik.values.hasDocument, formik.values._id, defaultValues.document)) {
      fetchPatientByCPF(cpfDigits);
    }

    if (cpfDigits.length === 0 && previousFormValuesRef.current?.document) {
      handleClearCPF();
    }

    if (cpfDigits.length > 0 && !validateCPF(cpfDigits)) {
      setShowMinorWarning(false);
      setShowApiUnavailableWarning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.document, formik.values.hasDocument]);

  const handleBirthDateValidationForLGPD = useCallback(
    (birthDate: string, document: string) => {
      if (!validateDateFromBrazilianForm(birthDate)) return;

      const cpfDigits = onlyDigits(document);
      if (!validateCPF(cpfDigits)) return;

      const birthDateISO = DateTime.fromFormat(birthDate, 'dd/MM/yyyy').toFormat('yyyy-MM-dd');
      fetchPatientByCPF(cpfDigits, birthDateISO);

      setLgpdBirthdateRequired(false);
    },
    [fetchPatientByCPF]
  );

  useEffect(() => {
    if (lgpdBirthdateRequired && formik.values.birthDate) {
      handleBirthDateValidationForLGPD(formik.values.birthDate, formik.values.document);
    }
  }, [lgpdBirthdateRequired, formik.values.birthDate, formik.values.document, handleBirthDateValidationForLGPD]);

  const handleClearCPF = () => {
    previousFormValuesRef.current = { ...formik.values };

    formik.setValues({
      ...initialValues,
      document: '',
      hasDocument: true,
    });

    setShowMinorWarning(false);
    setShowApiUnavailableWarning(false);
    setAutocompletedCPF(null);
  };

  useEffect(() => {
    if (locationState?.autocompleteData && locationState?.cpf) {
      const data = locationState.autocompleteData;

      if ('reason' in data) {
        formik.setFieldValue('document', locationState.cpf);
        formik.setFieldValue('hasDocument', true);

        setLastValidAutocompleteData({
          fullName: '',
          birthDate: '',
          cellphoneNumber: '',
          sex: GENDER.NOT_INFORMED,
          cpf: locationState.cpf,
          showMinorWarning: true,
        });
        setAutocompletedCPF(locationState.cpf);
        previousCPFRef.current = locationState.cpf;

        if (data.reason === 'lgpd_birthdate_required') {
          setLgpdBirthdateRequired(true);
          setShowMinorWarning(true);
        } else if (data.reason === 'lgpd_birthdate_mismatch' || data.reason === 'lgpd_patient_under_12') {
          setShowMinorWarning(true);
        }

        setShouldFocusAfterLoading(true);
        window.history.replaceState({}, document.title);
        return;
      }

      if ('patientData' in data) {
        const { patientData } = data;

        const birthDateFormatted = patientData.birthDate ? DateFromISOToBrazilianFormat(patientData.birthDate) : '';

        const isMinor = isPatientMinor(patientData.birthDate);
        setShowMinorWarning(isMinor);

        setLastValidAutocompleteData({
          fullName: patientData.fullName,
          birthDate: birthDateFormatted,
          cellphoneNumber: patientData.cellphoneNumber || '',
          sex: patientData.sex,
          cpf: locationState.cpf,
          showMinorWarning: isMinor,
        });

        setAutocompletedCPF(locationState.cpf);
        previousCPFRef.current = locationState.cpf;

        const newValues = {
          ...formik.values,
          fullName: patientData.fullName,
          birthDate: birthDateFormatted,
          cellphoneNumber: patientData.cellphoneNumber || '',
          sex: patientData.sex,
          document: locationState.cpf,
          hasDocument: true,
        };

        formik.setValues(newValues);

        requestAnimationFrame(() => {
          focusNextEmptyField(newValues);
        });

        window.history.replaceState({}, document.title);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState]);

  useEffect(() => {
    if (!props.formData) return;

    const patientId = get(props.formData, '_id', '');

    setValues({
      isFormOnEditMode: !!patientId,
      _id: patientId,
      fullName: props.formData.fullName,
      cellphoneNumber: props.formData.cellphoneNumber,
      birthDate: props.formData.birthDate ? props.formData.birthDate : '',
      sex: props.formData.sex,
      document: props.formData.document,
      documentType: props.formData.documentType,
      hasDocument: !!props.formData.document,
      active: props.formData.active,
      lastAttendance: props.formData.lastAttendance,
    });

    setDefaultValues({
      isFormOnEditMode: !!patientId,
      _id: patientId,
      fullName: props.formData.fullName,
      cellphoneNumber: props.formData.cellphoneNumber,
      birthDate: props.formData.birthDate ? props.formData.birthDate : '',
      sex: props.formData.sex,
      document: props.formData.document,
      documentType: props.formData.documentType,
      hasDocument: !!props.formData.document,
      active: props.formData.active,
      lastAttendance: props.formData.lastAttendance,
    });
  }, [setValues, props.formData]);

  if (props.isLoadingData) {
    return <PersonalDataFormSkeleton />;
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Flex
        flexDirection={'column'}
        gap={'20px'}
        bgColor={{
          base: 'transparent',
          lg: 'white',
        }}
        borderRadius={'20px'}
        p={{ base: '0px', sm: '32px' }}
        h={'full'}
      >
        <Box display={{ base: 'none', sm: 'block' }}>
          <Typography model="h2" weight="bold">
            Dados pessoais do paciente
          </Typography>
        </Box>

        {showApiUnavailableWarning && (
          <Flex
            border={'1px solid'}
            borderColor={'notification.info'}
            borderRadius={'10px'}
            alignItems={'center'}
            padding={'10px'}
            mb={'10px'}
            bgColor={'#E8F4FD'}
          >
            <Box mr={'5px'}>
              <AlertIcon variantColor="notification.info" width={12} />
            </Box>
            <Typography model="body" size="small" weight="regular">
              Não foi possível consultar o CPF. Preencha os dados manualmente para continuar.
            </Typography>
          </Flex>
        )}

        {showMinorWarning && (
          <Flex
            border={'1px solid'}
            borderColor={'notification.alert'}
            borderRadius={'10px'}
            alignItems={'center'}
            padding={'10px'}
            mb={'10px'}
            bgColor={'#FFF4E6'}
          >
            <Box mr={'5px'}>
              <AlertIcon variantColor="notification.alert" width={12} />
            </Box>
            <Typography model="body" size="small" weight="semi">
              LGPD: menores de 18 anos precisam ter os dados revisados pelo responsável; preencha os campos manualmente
              quando incompletos.
            </Typography>
          </Flex>
        )}

        <SimpleGrid columns={1}>
          <Box>
            {loadingAutocomplete ? (
              <Flex gap={'10px'} flexDirection={{ base: 'column', sm: 'row' }} mb={'20px'}>
                <Box flex={1} display={'flex'} flexDirection={'column'}>
                  <Box mb={'8px'}>
                    <Typography model="body" size="small" weight="regular">
                      CPF*
                    </Typography>
                  </Box>
                  <Skeleton height="45px" borderRadius="md" />
                  <Box mt={'12px'}>
                    <Skeleton height="20px" width="120px" borderRadius="md" />
                  </Box>
                </Box>
                <Box flex={1}>
                  <Box mb={'8px'}>
                    <Typography model="body" size="small" weight="regular">
                      DATA DE NASCIMENTO*
                    </Typography>
                  </Box>
                  <Skeleton height="45px" borderRadius="md" />
                </Box>
              </Flex>
            ) : (
              <Flex gap={'10px'} flexDirection={{ base: 'column', sm: 'row' }} mb={'20px'}>
                <Box flex={1} display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
                  <FormControl isInvalid={!!formik.errors.document}>
                    <MevoInput
                      id={FIELD_IDS.DOCUMENT}
                      labelText="CPF*"
                      type={'tel'}
                      h={'45px'}
                      placeholder={'000.000.000-00'}
                      name={'document'}
                      value={formik.values.document}
                      onChange={formik.handleChange}
                      onBlur={() => formik.validateField('document')}
                      errorText={formik.errors.document}
                      mask={MASKS.CPF}
                      disabled={shouldDisableCPFField(
                        formik.values.hasDocument,
                        formik.values._id,
                        defaultValues.document
                      )}
                      autoFocus
                    />
                  </FormControl>
                  <Checkbox
                    marginTop={formik.errors.document ? '10px' : '12px'}
                    name="hasDocument"
                    isChecked={!formik.values.hasDocument}
                    isDisabled={isEditingPatientWithCPF(formik.values._id, defaultValues.document)}
                    onChange={(e) => {
                      formik.setFieldValue('hasDocument', !e.target.checked);
                      if (e.target.checked) {
                        formik.setFieldValue('document', '');
                        formik.setErrors({ ...formik.errors, document: '' });
                        return;
                      }
                    }}
                  >
                    <Box mt={'2px'}>
                      <Typography model="body" size="normal" weight="regular">
                        Não possui CPF
                      </Typography>
                    </Box>
                  </Checkbox>
                </Box>

                <Box flex={1}>
                  <FormControl isInvalid={!!formik.errors.birthDate}>
                    <MevoInput
                      id={FIELD_IDS.BIRTH_DATE}
                      ref={birthDateInputRef}
                      labelText="DATA DE NASCIMENTO*"
                      type={'tel'}
                      h={'45px'}
                      placeholder={'00/00/0000'}
                      name={'birthDate'}
                      value={formik.values.birthDate}
                      onChange={formik.handleChange}
                      onBlur={() => formik.validateField('birthDate')}
                      errorText={formik.errors.birthDate}
                      mask={MASKS.BIRTH_DATE}
                      maskChar={''}
                    />
                  </FormControl>
                </Box>
              </Flex>
            )}

            {loadingAutocomplete ? (
              <Box mb={'20px'}>
                <Box mb={'8px'}>
                  <Typography model="body" size="small" weight="regular">
                    NOME COMPLETO*
                  </Typography>
                </Box>
                <Skeleton height="45px" borderRadius="md" />
              </Box>
            ) : (
              <FormControl isInvalid={!!formik.errors.fullName} mb={'20px'}>
                <MevoInput
                  id={FIELD_IDS.FULL_NAME}
                  ref={fullNameInputRef}
                  labelText="NOME COMPLETO*"
                  h={'45px'}
                  placeholderText={'Digite o nome'}
                  value={formik.values.fullName}
                  name={'fullName'}
                  onChange={formik.handleChange}
                  errorText={formik.errors.fullName}
                  maxLength={200}
                />
              </FormControl>
            )}

            {loadingAutocomplete ? (
              <Flex gap={'10px'} flexDirection={{ base: 'column', sm: 'row' }} mb={'20px'}>
                <Box flex={1}>
                  <Box mb={'8px'}>
                    <Typography model="body" size="small" weight="regular">
                      SEXO*
                    </Typography>
                  </Box>
                  <Skeleton height="45px" borderRadius="md" />
                </Box>
                <Box flex={1}>
                  <Box mb={'8px'}>
                    <Typography model="body" size="small" weight="regular">
                      CELULAR*
                    </Typography>
                  </Box>
                  <Skeleton height="45px" borderRadius="md" />
                </Box>
              </Flex>
            ) : (
              <Flex gap={'10px'} flexDirection={{ base: 'column', sm: 'row' }} mb={'20px'}>
                <FormControl isInvalid={!!formik.errors.sex}>
                  <SelectGender
                    label={'SEXO*'}
                    value={formik.values.sex}
                    onChange={(value) => formik.setFieldValue('sex', value)}
                    errorMessage={'Informe o sexo.'}
                  />
                </FormControl>

                <FormControl isInvalid={!!formik.errors.cellphoneNumber}>
                  <MevoInput
                    id={FIELD_IDS.CELLPHONE}
                    ref={cellphoneInputRef}
                    labelText="CELULAR*"
                    h={'45px'}
                    type={'tel'}
                    placeholderText={'(99) 99999-9999'}
                    name={'cellphoneNumber'}
                    value={formik.values.cellphoneNumber}
                    onChange={formik.handleChange}
                    errorText={formik.errors.cellphoneNumber}
                    mask={MASKS.BRAZIL_PHONE}
                    onBlur={() => {
                      formik.validateField('cellphoneNumber');
                    }}
                  />
                </FormControl>
              </Flex>
            )}
          </Box>
        </SimpleGrid>
        <Flex flexDirection={{ base: 'column', sm: 'row' }} justifyContent={'space-between'}>
          <Button
            type={'submit'}
            isLoading={formik.isSubmitting}
            isDisabled={isSubmitButtonDisabled}
            variant={isSubmitButtonDisabled ? 'outline-primary-light' : 'solid'}
            w={{
              base: 'full',
              md: '150px',
            }}
            h={'60px'}
            alignSelf={'flex-end'}
            mt={'auto'}
          >
            Salvar
          </Button>
        </Flex>
      </Flex>
    </form>
  );
}
